import { _ } from "../../core/polyglot/polyglot.js";
import { changeCulture } from "../../core/helpers/currency-formatting-util.js";
import {
	makeImportApiRepository,
	makeMonitorRepository,
} from "../../core/repositories/repositoryBuilders.js";
import { router } from "@foretagsplatsen/widgetjs";
import accountingDocument from "../shared/accountingDocument.js";
import Agency from "../../models/Agency.js";
import AgencyIntegrationSettings from "../../models/agencyIntegrationSettings/AgencyIntegrationSettings.js";
import ChartOfAccounts from "../../models/chartOfAccounts/ChartOfAccounts.js";
import Company from "../../models/Company.js";
import CompanyIntegrationSettings from "../../models/companyIntegrationSettings/CompanyIntegrationSettings.js";
import CompanySettingsDocumentSection from "../../core/documentSections/CompanySettingsDocumentSection.js";
import CompanyUser from "../../models/CompanyUser.js";
import documentServiceModel from "../../core/repositories/document/documentService.js";
import FiscalYear from "../../models/period/FiscalYear.js";
import ForecastTemplateService from "../../core/services/ForecastTemplateService.js";
import getKeyFigureService from "../../core/services/getKeyFigureService.js";
import optionsBag from "../../models/options/optionsBag.js";
import session from "../../core/session.js";
import UsersWidget from "../../core/widgets/UsersWidget/UsersWidget.js";

/**
 * A document with settings for the company
 *  - View and edit fiscal years
 *  - View and edit company details
 *  - View, remove and edit existing users
 *  - Add new users
 *
 * @param {{}} spec - Parameter object
 * @param {*} my - protected fields holder
 * @return {*}
 */
function companySettingsDocument(spec, my) {
	spec ||= {};
	my ||= {};

	const that = accountingDocument(spec, my);
	const currentUser = session.getCurrentUser();
	const company = session.getCompany();
	const agency = session.agency;
	const country = session.getCountry();
	const antiForgeryToken = session.getAntiForgeryToken();

	const chartOfAccountsEditorFeatureIsActive = optionsBag
		.fromId("coa-editor")
		.isActive();

	const documentArchiveFeatureIsActive = optionsBag
		.fromId("document-archive")
		.isActive();

	const customKeyFigureEditorFeatureIsActive = optionsBag
		.fromId("keyfigure-editor")
		.isActive();

	const isForecastingEnabled = optionsBag.fromId("forecasting").isActive();

	that.documentId = "accounting company-settings settings";

	my.forecastTemplateService = new ForecastTemplateService({
		company,
		countryId: country.getId(),
	});

	my.agency = Agency.materialize(session.agency);

	my.isAgencyChartOfAccounts = function (chartOfAccounts) {
		return chartOfAccounts.belongsTo(my.agency);
	};

	my.canDeleteChartOfAccounts = function (coa) {
		if (!coa.isUserDefined()) return false;

		if (my.isAgencyChartOfAccounts(coa))
			return currentUser.canDeleteAgencyChartOfAccounts();

		return true;
	};

	my.companyUserRepository = makeMonitorRepository({
		route: "/Shared/Company/#companyId/User/?id",
		model: CompanyUser,
		defaultFilters: {
			companyId: session.getCompany().getLegacyId(),
		},
		antiForgeryToken,
	});

	my.agencyIntegrationSettingsRepository = makeImportApiRepository({
		model: AgencyIntegrationSettings,
		route: "/Countries/#countryId/Agencies/#agencyId/AgencyIntegrationSettings",
		defaultFilters: {
			countryId: session.getCountryReferentialApiId(),
			agencyId: my.agency.getReferentialApiId(),
		},
	});

	my.companyIntegrationSettingsRepository = makeImportApiRepository({
		model: CompanyIntegrationSettings,
		route: "/Countries/#countryId/Agencies/#agencyId/Companies/#companyId/Settings",
		defaultFilters: {
			countryId: session.getCountryReferentialApiId(),
			agencyId: my.agency.getReferentialApiId(),
			companyId: company.getReferentialApiId(),
		},
	});

	that.title = _("Settings for {company}", { company: company.getName() });

	that.help = function () {
		return [
			{
				title: _("Company Settings"),
				content: _(
					"This is the page where the company settings are made. Fiscal years, contact info and users attached to the company is listed.",
				),
			},
			{
				title: _("Company information"),
				content: _("Contact information for the company is displayed."),
				element: "company-info",
			},
			{
				title: _("Fiscal years"),
				content: _(
					"All imported fiscal years can be accessed. These can also be edited as well as new account information can be imported to these fiscal years.",
				),
				element: "fiscal-years",
			},
			{
				title: _("Select Active period"),
				content: _(
					"To select an Active period is to limit which month that should be the last to be shown when browsing the reports and KPIs. For example, there can be bookkeeping data imported to July 31th but the person responsible for reporting can limit the reports to show data up to June 30th. An administrator or consultant can use this feature to hide periods ranging from a single month to a full fiscal year.",
				),
				visible: currentUser.canSeeInactivePeriod(company),
			},
			{
				title: _("Preliminary documents"),
				content: _(
					'Users with the roles Agency director or Consultant can mark uploaded documents as being "preliminary". A preliminary document is not visible for any client user, even for Company Administrator users with otherwise full access to the document archive.',
				),
				visible: currentUser.canEditPreliminaryDocuments(company),
			},
			{
				title: _("Users"),
				content: _(
					"All users connected to the company can be edited from this page.",
				),
			},
			{
				title: _("Chart of Accounts"),
				visible: chartOfAccountsEditorFeatureIsActive,
				content: _(
					"A list of chart of accounts is displayed. They can be edited and saved with a new name and will then appear in this list.",
				),
			},
		];
	};

	that.hasActionsMenu = function () {
		return false;
	};

	that.hasPeriodPicker = function () {
		return false;
	};

	that.show = async function () {
		const agencyIntegrationSettings =
			await my.fetchAgencyIntegrationSettings();

		const companyIntegrationSettings =
			await my.fetchCompanyIntegrationSettings();

		my.companySettingsWidget = new CompanySettingsDocumentSection({
			currentUser,
			company,
			country,
			showChartOfAccountsEditor: chartOfAccountsEditorFeatureIsActive,
			canDeleteChartOfAccounts: my.canDeleteChartOfAccounts,
			isAgencyChartOfAccounts: my.isAgencyChartOfAccounts,
			showCustomKeyFigures: customKeyFigureEditorFeatureIsActive,
			isDocumentsEnabled: documentArchiveFeatureIsActive,
			isForecastingEnabled,
			isArchiveCompanyEnabled: false,
			isManageUsersInUiDisabled: agency.isManageUsersInUiDisabled,
			usersWidget: new UsersWidget({
				company,
				currentUser: my.currentUser,
			}),
			documentService: documentServiceModel.forCompany(
				session.getCompany(),
			),
			viewKeyFigureCallback: (keyFigure) =>
				my.redirectTo("single-kpi", { keyFigure: keyFigure.getId() }),
			redirectToKeyFigureEditor: (keyFigure) =>
				my.redirectTo(
					"kpi-editor",
					keyFigure && { keyFigureId: keyFigure.getId() },
				),
			keyFigureService: getKeyFigureService(),
			onEditKeyFigurePreset: (preset) =>
				my.redirectTo("edit-preset", { id: preset.getId() }),
			capegoSectionEnabled: agencyIntegrationSettings
				?.getCapegoIntegrationSettings()
				?.hasOfficeCode(),
			integrationSettings: companyIntegrationSettings,
			onIntegrationSettingsChange: () =>
				my.saveIntegrationSettings({ companyIntegrationSettings }),
		});

		await my.setupCompanySettingsWidget();
		await my.updateCompanyUsersWidget();

		my.companySettingsWidget.show();
		that.update();
	};

	my.updateBudget = function (budget) {
		// Get a shallow copy of the budget. But without items.
		// This prevents the server from aptching the budget with
		// an empty collection of items.
		const budgetToStore = budget.copyBudgetWithoutItems();

		return my.budgetRepository.update({
			filter: { id: budgetToStore.getId() },
			model: budgetToStore,
			successMessage: null,
		});
	};

	my.updateFiscalYear = function (fiscalYear) {
		return my.fiscalYearRepository.update({
			filter: { id: fiscalYear.getId() },
			model: fiscalYear,
			successMessage: null,
		});
	};

	my.chartOfAccountsRepository = makeMonitorRepository({
		route: "/Shared/Company/#companyId/ChartOfAccounts/?id",
		model: ChartOfAccounts,
		defaultFilters: {
			companyId: session.getCompany().getLegacyId(),
		},
		antiForgeryToken,
	});

	my.budgetRepository = makeMonitorRepository({
		route: "/Accounting2/Company/#companyId/Budget/?id",
		defaultFilters: { companyId: session.getCompany().getId() },
		antiForgeryToken,
	});

	my.integrationRepository = makeMonitorRepository({
		route: "/Shared/Company/#companyId/integration/#integrationType",
		antiForgeryToken,
	});

	my.companyRepository = makeMonitorRepository({
		route: "/Shared/Company/#companyId",
		model: Company,
		defaultFilters: { companyId: company.getLegacyId() },
		antiForgeryToken,
	});

	my.fiscalYearRepository = makeMonitorRepository({
		route: "/Shared/Company/#companyId/FiscalYear/?id",
		defaultFilters: { companyId: company.getLegacyId() },
		model: FiscalYear,
		antiForgeryToken,
	});

	my.dataExtractionRepository = makeMonitorRepository({
		route: "/Shared/Company/#companyId/DataExtraction",
		antiForgeryToken,
	});

	my.setupCompanySettingsWidget = async function () {
		my.companySettingsWidget.onChartOfAccountsImported.register((coa) => {
			coa.ownerType = ChartOfAccounts.getDefaultOwnerType();

			my.chartOfAccountsRepository
				.save({
					model: coa,
				})
				// eslint-disable-next-line no-shadow -- bad practice, we should fix this
				.then((coa) => {
					my.redirectTo("chart-of-accounts-editor/?id", {
						id: coa.getId(),
					});
				});
		});

		my.companySettingsWidget.onChartOfAccountsDeleted.register(
			async (coa, onSuccess, onError) => {
				try {
					await my.chartOfAccountsRepository.remove({
						model: coa,
						notify: false,
						filter: {
							id: coa.getId(),
						},
					});

					onSuccess();
				} catch (error) {
					const chartsOfAccounts =
						await my.chartOfAccountsRepository.get();
					onError({ chartsOfAccounts, error });
				}
			},
		);

		my.companySettingsWidget.onChartOfAccountsClicked.register(
			(chartOfAccounts) => {
				my.redirectTo("chart-of-accounts-editor/?id", {
					id: chartOfAccounts.getId(),
				});
			},
		);

		my.companySettingsWidget.onFiscalYearDeleted.register(
			(year, onSuccess) => {
				my.fiscalYearRepository
					.remove({
						filter: { id: year.getId() },
						successMessage: null,
					})
					.then(onSuccess);
			},
		);

		my.companySettingsWidget.onBudgetDeleted.register(
			// eslint-disable-next-line no-shadow -- bad practice, we should fix this
			(budget, company, onSuccess) => {
				my.budgetRepository
					.remove({
						successMessage: _("Budget is removed successfully"),
						filter: { id: budget.getId() },
					})
					.then(onSuccess);
			},
		);

		my.companySettingsWidget.onFiscalYearSaved.register((fiscalYear) => {
			const onFiscalYearUpdateSuccess = () =>
				router.getRouter().reloadPage();

			if (session.getPeriod().getYear().equals(fiscalYear)) {
				changeCulture(fiscalYear.getCurrencyCultureName());
			}

			const currentBudget = fiscalYear.getCurrentBudget();

			if (currentBudget) {
				my.updateFiscalYear(fiscalYear)
					.then(() => my.updateBudget(currentBudget))
					.then(onFiscalYearUpdateSuccess);
			} else {
				my.updateFiscalYear(fiscalYear).then(onFiscalYearUpdateSuccess);
			}
		});

		my.companySettingsWidget.onUserCreated.register((userToCreate) => {
			my.companyUserRepository
				.save({
					model: userToCreate,
				})
				.then(my.updateCompanyUsersWidget);
		});

		my.companySettingsWidget.onUserDeleted.register((userToRemove) => {
			my.companyUserRepository
				.remove({ filter: { id: userToRemove.getId() } })
				.then(my.updateCompanyUsersWidget);
		});

		my.companySettingsWidget.onCompanySaved.register(
			(updatedCompany, onSuccess = () => {}) => {
				my.companyRepository
					.update({ model: updatedCompany })
					.then(onSuccess);
			},
		);

		my.companySettingsWidget.onCompanyDeleted.register((deletedCompany) => {
			my.companyRepository.remove().then(() => {
				my.redirectToLocationPath(
					`/Navigate/ToAgency?agencyId=${deletedCompany.getAgencyId()}`,
				);
			});
		});

		my.companySettingsWidget.onCompanyDataExtractionRequested.register(
			// eslint-disable-next-line no-shadow -- bad practice, we should fix this
			(company) => {
				my.requestCompanyDataExtraction(company.getLegacyId());
			},
		);

		my.companySettingsWidget.onIntegrationDeleted.register(
			(integration) => {
				my.integrationRepository.remove({
					filter: {
						companyId: company.getLegacyId(),
						integrationType: integration.getType(),
					},
					successMessage: _(
						"The integration was disconnected successfully",
					),
				});
			},
		);

		try {
			const chartsOfAccounts = await my.chartOfAccountsRepository.get();
			chartsOfAccounts.sort((a, b) => a.compare(b));
			my.companySettingsWidget.setChartsOfAccounts(chartsOfAccounts);
		} catch (error) {
			if (!error.isRepositoryError) throw error;
		}

		if (isForecastingEnabled) {
			const chartOfAccountsId = session.getCurrentChartOfAccountsId();

			const defaultForecastTemplatesConfiguration =
				await my.forecastTemplateService.getDefaultForecastTemplatesConfiguration(
					{ chartOfAccountsId },
				);

			my.companySettingsWidget.setForecastTemplateConfiguration(
				defaultForecastTemplatesConfiguration,
			);
		}
	};

	my.updateCompanyUsersWidget = async function () {
		if (!my.companySettingsWidget) {
			return;
		}

		try {
			const users = await my.companyUserRepository.get();
			my.companySettingsWidget.setUsers(users);
		} catch (error) {
			if (!error.isRepositoryError) throw error;
		}
	};

	my.requestCompanyDataExtraction = function (companyId) {
		my.dataExtractionRepository.save({
			filter: { companyId },
			errorMessage: _("This company has no data to extract."),
		});
	};

	my.fetchAgencyIntegrationSettings = async function () {
		try {
			return await my.agencyIntegrationSettingsRepository.get({
				notify: false,
			});
		} catch (error) {
			if (error.isRepositoryError) {
				// it's ok if we can't reach
				// agencyIntegrationSettingsRepository, we just don't
				// display the corresponding widget
				return null;
			}

			throw error;
		}
	};

	my.fetchCompanyIntegrationSettings = async function () {
		try {
			return await my.companyIntegrationSettingsRepository.get({
				notify: false,
			});
		} catch (error) {
			if (error.isRepositoryError) {
				// it's ok if we can't reach
				// agencyIntegrationSettingsRepository, we just don't
				// display the corresponding widget
				return null;
			}

			throw error;
		}
	};

	my.saveIntegrationSettings = function ({ companyIntegrationSettings }) {
		my.companyIntegrationSettingsRepository.update({
			model: companyIntegrationSettings,
		});
	};

	my.renderContentOn = function (html) {
		if (!my.companySettingsWidget) return;

		html.div(
			{
				klass: "company-settings",
			},
			my.section({ content: my.companySettingsWidget }),
		);
	};

	return that;
}

export default companySettingsDocument;
